import React from 'react';
import { shape, bool } from 'prop-types';
import { gql, graphql, compose } from 'react-apollo';
import { connect } from 'react-redux';
import IconButton from 'components/IconButton';
import DeviceStatusText from 'components/DeviceStatusText';
import LoadingIndicator from 'components/LoadingIndicator';
import BatteryIcon from 'components/BatteryIcon';
import formatNextUpdate from 'helpers/formatNextUpdate';
import formatLastUpdate from 'helpers/formatLastUpdate';
import formatDaysHoursMinutesSeconds from 'helpers/formatDaysHoursMinutesSeconds';
import formatMinutesSeconds from 'helpers/formatMinutesSeconds';
import formatInteger from 'helpers/formatInteger';
import ChevronIcon from 'icons/chevron.inline.svg';

import Layout from '../Layout';
import LocationHeader from '../LocationHeader';
import DeviceContainer from '../DeviceContainer';
import HorizontalSection from '../../HorizontalSection';
import TripletRow from '../../TripletRow';
import TripletItem from '../../TripletItem';

import Chart from '../Chart';
import GridRow from '../../GridRow';
import GridItem from '../../GridItem';

const LocationQuery = gql`
  query Location($id: ID!) {
    location(id: $id) {
      id
      lat
      lng
      name
      trackingDevice {
        id
        name
        status
        batteryVoltage
        temperature
        lastUpdate
        reportInterval
        numberOfHitsToday
        reportedHitsToday {
          timestamp
          value
        }
        uptime
      }
    }
  }
`;

export function Location({ data }) {
  if (data.loading) {
    return (
      <Layout
        backTo="/map/live/locations"
        title="Location"
        header={<LocationHeader status="ok" name="..." isLoading />}
      >
        <LoadingIndicator />
      </Layout>
    );
  }

  const status = data.location.trackingDevice.status;

  const { name, trackingDevice } = data.location;
  const {
    id: deviceId,
    numberOfHitsToday,
    reportedHitsToday,
    lastUpdate,
    reportInterval,
    batteryVoltage,
    temperature,
    uptime,
  } = trackingDevice;

  const isDeviceOnline = status !== 'disconnected';

  return (
    <Layout
      backTo="/map/live/locations"
      title="All locations"
      header={<LocationHeader status={status} name={name} />}
    >
      <DeviceContainer status={status}>
        <TripletRow title="Device">
          <TripletItem title="name">
            {trackingDevice.name}
          </TripletItem>
          <TripletItem title="status" status={status} containsDevice>
            <DeviceStatusText status={status} />
          </TripletItem>
        </TripletRow>

        <Chart
          title="Number of hits today"
          reportedValues={reportedHitsToday}
          calculateCrosshairA={({ reported }) => ({
            value: formatInteger(reported),
            title: 'hits',
          })}
        />

        <GridRow title="Location">
          <GridItem label="hits today">
            {formatInteger(numberOfHitsToday)}
          </GridItem>
          {isDeviceOnline
            ? <GridItem label="next update">
                {formatNextUpdate(lastUpdate, reportInterval)}
              </GridItem>
            : <GridItem label="last update">
                {formatLastUpdate(lastUpdate)}
              </GridItem>}
          <GridItem label="report interval">
            {formatMinutesSeconds(reportInterval)}
          </GridItem>
        </GridRow>
        <GridRow title="Device">
          <GridItem label={`${batteryVoltage}V`}>
            <BatteryIcon batteryVoltage={batteryVoltage} />
          </GridItem>
          <GridItem label="device temp">
            {`${temperature}°F`}
          </GridItem>

          <GridItem label="uptime">
            {uptime > 0 ? formatDaysHoursMinutesSeconds(uptime) : 'None'}
          </GridItem>
        </GridRow>
        <HorizontalSection center>
          <IconButton
            label="details page"
            linkTo={`/segments/${deviceId}`}
            disabled
            asLink
            iconRight
          >
            <ChevronIcon />
          </IconButton>
        </HorizontalSection>
      </DeviceContainer>
    </Layout>
  );
}

Location.propTypes = {
  data: shape({
    loading: bool.isRequired,
    location: shape({}),
  }).isRequired,
};

const mapStateToProps = ({ map }, ownProps) => ({
  currentHover: map.currentHover,
  ...ownProps,
});

const ConnectedLocationWithData = compose(
  connect(mapStateToProps),
  graphql(LocationQuery, {
    options: ({ params }) => ({
      variables: {
        id: params.id,
      },
    }),
  })
)(Location);

export default ConnectedLocationWithData;



// WEBPACK FOOTER //
// ./src/components/DataPanel/Details/Location/index.js